import { Inbox, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useCurrentUser, useInbox } from "@/api/hooks";
import { HelpPopover } from "@/components/HelpPopover";
import { formatTime } from "@/lib/format";

/** Org-wide queue of quotes awaiting a contractor reply.
 *
 * Each row = one quote where the latest comment is from a client
 * (i.e. the contractor hasn't replied yet). Click → quote detail
 * page where they can read the full thread + post a response. The
 * inbox refetches every 30s; posting a reply auto-clears the row. */
export function InboxPage() {
  const me = useCurrentUser();
  const inbox = useInbox();
  const isPencilOutAdmin = me.data?.role === "pencilout_admin";

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-1.5">
          <h1 className="font-mono text-2xl font-bold tracking-tight">Inbox</h1>
          <HelpPopover title="What lands in your Inbox">
            <p>
              Every quote where a homeowner has asked a question or left a note that you haven't
              replied to yet.
            </p>
            <p>
              Comments come in two ways: from the public quote page (the homeowner clicks "Add
              comment" on the quote view) OR by email reply (their reply to your quote email gets
              parsed and threaded here automatically — no extra step needed).
            </p>
            <p>
              Tap any row to see the full thread + post a response. Posting a reply clears the row
              from the queue.
            </p>
          </HelpPopover>
        </div>
        <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
          {isPencilOutAdmin
            ? "Every quote across every organization that's waiting on a contractor reply."
            : "Quotes from your organization where a client has asked a question or left a note that hasn't been answered yet."}
        </p>
      </div>

      {inbox.isLoading && !inbox.data && (
        <p className="text-sm text-graphite-soft">Loading inbox…</p>
      )}

      {inbox.error && !inbox.data && (
        <p className="text-sm text-red-pencil">
          {inbox.error instanceof ApiError ? inbox.error.detail : "Could not load inbox."}
        </p>
      )}

      {inbox.data && inbox.data.length === 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm p-12 text-center">
          <Inbox className="mx-auto h-8 w-8 text-graphite-faint" aria-hidden />
          <p className="mt-4 text-graphite-soft">
            All caught up — no client questions or comments waiting for a reply.
          </p>
        </div>
      )}

      {inbox.data && inbox.data.length > 0 && (
        <ul className="space-y-2">
          {inbox.data.map((item) => (
            <li
              key={item.quote_id}
              className="rounded-lg border border-rule bg-paper-warm transition hover:bg-pencil-yellow-soft"
            >
              <Link
                to={`/quotes/${item.quote_id}`}
                className="flex flex-wrap items-start justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <MessageCircle
                      className="h-3.5 w-3.5 flex-none text-graphite-soft"
                      aria-hidden
                    />
                    <span className="font-medium text-graphite">{item.latest_comment_author}</span>
                    {item.unanswered_count > 1 && (
                      <span className="rounded bg-pencil-yellow px-1.5 py-0.5 font-mono text-[10px] font-bold text-graphite">
                        {item.unanswered_count} new
                      </span>
                    )}
                    <span className="text-graphite-soft">·</span>
                    <span className="text-sm text-graphite-soft">{item.job_title}</span>
                    {isPencilOutAdmin && (
                      <>
                        <span className="text-graphite-faint">·</span>
                        <span className="font-mono text-xs text-graphite-soft">
                          {item.organization_name}
                        </span>
                      </>
                    )}
                  </div>
                  <p className="mt-1 truncate text-sm text-graphite-soft">
                    {item.latest_comment_excerpt}
                  </p>
                </div>
                <span className="font-mono text-xs text-graphite-soft tabular-nums">
                  {formatTime(item.latest_comment_at)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
